import { inject, Injectable, signal } from '@angular/core';
import { GoogleGenAI, type LiveServerMessage, type Session } from '@google/genai';
import { Subject } from 'rxjs';

import { EphemeralTokenService } from './ephemeral-token.service';
import { buildLiveConnectConfig, GEMINI_MODEL } from './gemini-live-config';

const MAX_RECONNECT_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;
const HALLUCINATION_GUARD_MS = 5000;

@Injectable({ providedIn: 'root' })
export class GeminiLiveService {
  private readonly ephemeralTokenService = inject(EphemeralTokenService);

  readonly isConnected = signal(false);
  readonly isReconnecting = signal(false);

  private readonly audioChunkSubject = new Subject<string>();
  private readonly textResponseSubject = new Subject<string>();
  private readonly toolCallSubject = new Subject<any>();
  private readonly modelTranscriptionSubject = new Subject<string>();
  private readonly transcriptionSubject = new Subject<string>();
  private readonly turnCompleteSubject = new Subject<void>();
  private readonly interruptedSubject = new Subject<void>();
  private readonly errorSubject = new Subject<string>();

  readonly audioChunk$ = this.audioChunkSubject.asObservable();
  readonly textResponse$ = this.textResponseSubject.asObservable();
  readonly toolCall$ = this.toolCallSubject.asObservable();
  readonly modelTranscription$ = this.modelTranscriptionSubject.asObservable();
  readonly transcription$ = this.transcriptionSubject.asObservable();
  readonly turnComplete$ = this.turnCompleteSubject.asObservable();
  readonly interrupted$ = this.interruptedSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  private session: Session | null = null;
  private resumptionHandle: string | null = null;
  private connectedAt = 0;
  private intentionallyClosed = false;

  async connect(): Promise<void> {
    this.intentionallyClosed = false;
    const token = await this.ephemeralTokenService.getToken();
    await this.connectSession(token, null);
    this.triggerSessionStart();
  }

  disconnect(): void {
    this.intentionallyClosed = true;
    this.session?.close();
    this.session = null;
    this.resumptionHandle = null;
    this.isConnected.set(false);
    this.isReconnecting.set(false);
  }

  sendAudio(base64Data: string): void {
    this.session?.sendRealtimeInput({
      audio: { data: base64Data, mimeType: 'audio/pcm;rate=16000' },
    });
  }

  sendVideo(base64Data: string): void {
    this.session?.sendRealtimeInput({
      video: { data: base64Data, mimeType: 'image/jpeg' },
    });
  }

  sendText(message: string): void {
    console.log('[GeminiLive] sendText called, session exists:', !!this.session);
    this.session?.sendClientContent({
      turns: [{ role: 'user', parts: [{ text: message }] }],
      turnComplete: true,
    });
  }

  sendContext(message: string): void {
    this.session?.sendClientContent({
      turns: [{ role: 'user', parts: [{ text: message }] }],
      turnComplete: false,
    });
  }

  sendToolResponse(id: string, name: string, result: string): void {
    this.session?.sendToolResponse({
      functionResponses: [{ id, name, response: { result } }],
    });
  }

  private async connectSession(token: string, resumptionHandle: string | null): Promise<void> {
    const ai = new GoogleGenAI({ apiKey: token, httpOptions: { apiVersion: 'v1alpha' } });
    const config = buildLiveConnectConfig(resumptionHandle);

    this.session = await ai.live.connect({
      model: GEMINI_MODEL,
      config,
      callbacks: {
        onopen: () => {
          this.connectedAt = Date.now();
          this.isConnected.set(true);
          this.isReconnecting.set(false);
        },
        onmessage: (msg: LiveServerMessage) => {
          this.handleServerMessage(msg);
        },
        onerror: (e: ErrorEvent) => {
          this.errorSubject.next('Voice connection error');
        },
        onclose: (_e: CloseEvent) => {
          if (this.intentionallyClosed) {return;}
          this.isReconnecting.set(true);
          this.attemptReconnection(token);
        },
      },
    });
  }

  private handleServerMessage(msg: LiveServerMessage): void {
    if (msg.toolCall) {
      const names = msg.toolCall.functionCalls?.map((fc: any) => fc.name) || [];
      console.log('[GeminiLive] Server message with toolCall:', names.join(', '));
    }
    if (msg.serverContent?.modelTurn?.parts?.some((p: any) => p.inlineData?.data)) {
      console.log('[GeminiLive] Server message with audio chunk');
    }
    if (msg.serverContent?.turnComplete) {
      console.log('[GeminiLive] Turn complete');
    }

    if (msg.sessionResumptionUpdate?.newHandle) {
      this.resumptionHandle = msg.sessionResumptionUpdate.newHandle;
      return;
    }

    const elapsed = Date.now() - this.connectedAt;
    if (elapsed < HALLUCINATION_GUARD_MS && msg.toolCall) {
      const isHistoryCall = msg.toolCall.functionCalls?.some(
        (fc: any) => fc.name === 'get_conversation_history',
      );
      if (!isHistoryCall) {
        console.warn(`[GeminiLive] Dropping hallucinated tool call received ${elapsed}ms after connection`);
        return;
      }
    }

    const parts = msg.serverContent?.modelTurn?.parts;
    if (parts) {
      for (const part of parts) {
        if (part.inlineData?.data) {
          this.audioChunkSubject.next(part.inlineData.data);
        }
        if (part.text) {
          this.textResponseSubject.next(part.text);
        }
      }
    }

    if (msg.toolCall) {
      this.toolCallSubject.next(msg.toolCall);
    }

    if (msg.serverContent?.outputTranscription?.text) {
      this.modelTranscriptionSubject.next(msg.serverContent.outputTranscription.text);
    }

    if (msg.serverContent?.inputTranscription?.text) {
      this.transcriptionSubject.next(msg.serverContent.inputTranscription.text);
    }

    // The user talked over the model: the rest of the turn was never generated.
    if (msg.serverContent?.interrupted) {
      this.interruptedSubject.next();
    }

    if (msg.serverContent?.turnComplete) {
      this.turnCompleteSubject.next();
    }
  }

  private triggerSessionStart(): void {
    this.session?.sendClientContent({
      turns: [{ role: 'user', parts: [{ text: 'Session started.' }] }],
      turnComplete: true,
    });
  }

  private async attemptReconnection(token: string): Promise<void> {
    for (let attempt = 1; attempt <= MAX_RECONNECT_ATTEMPTS; attempt++) {
      const backoff = BASE_BACKOFF_MS * 2 ** (attempt - 1);
      await new Promise(resolve => setTimeout(resolve, backoff));

      if (this.intentionallyClosed) {return;}

      try {
        await this.connectSession(token, this.resumptionHandle);
        return;
      } catch {
        // Reconnection attempt failed, will retry
      }
    }

    this.isConnected.set(false);
    this.isReconnecting.set(false);
    this.errorSubject.next('Voice connection lost');
  }
}
